import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * ProjectScopingPage Component
 * 
 * Displays project scoping tools content like the legacy HTML site
 */
const ProjectScopingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Project Scoping intro content from the legacy HTML
  const projectScopingIntroHtml = `
    <p>
    Now that we have discovered and prioritised the problem, we need a consistent method of moving the project forward, particularly as more people come in and out of the project in order to keep them all aligned. 
    </p>
    
    <p>
    The tools in this section can help out with the project scoping process, ensuring an agreed upon plan before any significant resource is invested in to the project.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const projectCanvasTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/problem/project-scoping/project-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kogauXM=/" className="link-table">Miro</a>
    </>
  );

  const leanCanvasTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/problem/project-scoping/lean-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_knFghvg=/" className="link-table">Miro</a>
    </>
  );

  const projectBriefTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/problem/project-scoping/project-brief.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_knh7e64=/?&pres=1" className="link-table">Miro</a>
    </>
  );

  const deliverablesTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/problem/project-scoping/deliverables.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_knu7jLs=/?&pres=1" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const simonStubbenCreator = <a href="http://www.projectcanvas.dk">Simon Stubben</a>;
  const ashMauryaCreator = <a href="https://twitter.com/ashmaurya">Ash Maurya</a>;
  const naCreator = <a href="">N/A</a>;

  return (
    <>
      {/* Project Scoping Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/problem">Problem</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/problem/project-scoping">Project Scoping</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={projectScopingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#project-canvas">Project Canvas</Link></div>
            <div><Link className="tool-section-cta" to="#lean-canvas">Lean Canvas</Link></div>
            <div><Link className="tool-section-cta" to="#project-brief">Project Brief</Link></div>
            <div><Link className="tool-section-cta" to="#deliverables">Deliverables</Link></div>
          </div>
        </div>
      </div>

      {/* Project Canvas Section */}
      <main className="case-study">
        <span className="case-tool-title" id="project-canvas">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="project-canvas">
            <span className="tool-title-number">
              <Link to="#project-canvas">Project Canvas</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/project-scoping">Project Scoping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#project-canvas"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-project-canvas" 
            id="tab-project-canvas-overview" 
            defaultChecked
          />
          <label htmlFor="tab-project-canvas-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Project Scoping", "20/08/20", projectCanvasTemplates, simonStubbenCreator)}
              <div className="tool-opening">
                <h4>Business Analysis</h4>   
                <p>
                Part of the research phase requires introspection - understanding the current state of play, the context of the project, how the organisation operates and how any product development will align with the overarching strategy. 
                </p>
    
                <p>
                There are a number of elements that are part of the structure of an organisation - understanding these will help with clarifying what direction the project needs to go in and ultimately help increase the chances of a successful path forward.
                    
                    <ul className="bullet-tk">
                        <li>Budget and Timeline</li>
                        <li>Stakeholders</li>
                        <li>Customers</li>
                        <li>Success metrics</li>
                        <li>Competitive context</li> 
                        <li>Brand and culture</li>
                        <li>Business model</li>
                    </ul>
                </p>
    
                <h4>Project Canvas</h4>
    
                <p>
                A helpful tool that can align the various functions of a business, with the proposed opportunity/problem solving process is a Project Canvas. Taking what we've discovered in the first phase, we can begin to plot that opportunity with the business strategy, resources, goals and direction. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://experiencinginformation.com/2012/08/05/the-project-canvas/" className="link-table">The Project Canvas</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://canvanizer.com/new/project-canvas" className="link-table">Create a new Project Canvas</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-project-canvas" 
            id="tab-project-canvas-template"
          />
          <label htmlFor="tab-project-canvas-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kogauXM=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Project Canvas Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Lean Canvas Section */}
      <main className="case-study">
        <span className="case-tool-title" id="lean-canvas">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="lean-canvas">
            <span className="tool-title-number">
              <Link to="#lean-canvas">Lean Canvas</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/project-scoping">Project Scoping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#lean-canvas"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-lean-canvas" 
            id="tab-lean-canvas-overview" 
            defaultChecked
          />
          <label htmlFor="tab-lean-canvas-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Project Scoping", "20/09/20", leanCanvasTemplates, ashMauryaCreator)}
              <div className="tool-opening">
                <p>
                An updated, or modified version of the Business Model Canvas, a Lean Canvas seeks to emphasise the problem definition aspect of the project with the aim of solving wasted resources in developing products that aren't needed or wanted.
                </p>
                
                <p>
                Comprised of 9 'building blocks', it encourages rapid business planning that are easy to digest for the audience - clearly defining the problem, the goal and plan for the project.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://leanstack.com/lean-canvas" className="link-table">Capture your Business Model on a Lean Canvas</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://canvanizer.com/new/lean-canvas" className="link-table">Lean Canvas Creator</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://blog.leanstack.com/why-lean-canvas-vs-business-model-canvas-af62c0f250f0" className="link-table">Why Lean Canvas vs Business Model Canvas?</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://medium.com/@steve_mullen/an-introduction-to-lean-canvas-5c17c469d3e0" className="link-table">An Introduction to Lean Canvas</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-lean-canvas" 
            id="tab-lean-canvas-template"
          />
          <label htmlFor="tab-lean-canvas-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_knFghvg=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Lean Canvas Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Project Brief Section */}
      <main className="case-study">
        <span className="case-tool-title" id="project-brief">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="project-brief">
            <span className="tool-title-number">
              <Link to="#project-brief">Project Brief</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/project-scoping">Project Scoping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#project-brief"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-project-brief" 
            id="tab-project-brief-overview" 
            defaultChecked
          />
          <label htmlFor="tab-project-brief-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Project Scoping", "20/08/20", projectBriefTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                A brief is a great starting point for aligning all stakeholders when a project is first being discussed and all will agree to the brief before the project commences. 
                </p>
    
                <p>
                The brief will largely cover what's expected throughout the project, and importantly what the deliverables are.  
                </p>
                
                <p>
                Whilst a brief is a flexible document, it usually includes the following areas for clarity;
                </p>    
                
                <ul className="bullet-tk">
                    <li>Project Summary</li>
                    <li>Company Overview</li>
                    <li>Product Overview</li>
                    <li>Target User</li>
                    <li>Goals and Objectives</li>
                    <li>Constraints</li>
                    <li>Suggested Process and Tools
                        <ul className="bullet-tk nested-bullet">
                            <li>Phase 1 - Problem</li>
                            <li>Phase 2 - Research</li>
                            <li>Phase 3 - Synthesis</li>
                            <li>Phase 4 - Ideation</li>
                            <li>Phase 5 - Design</li>
                        </ul>
                    </li>
                    <li>Project Deliverables</li>
                    <li>Schedule and Budget</li>
                    <li>Contacts/Stakeholders</li>
                </ul>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.youtube.com/watch?v=VDpxa_KDIZg" className="link-table">How to Write a Creative Brief in 10 Minutes (With An Example)</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://blog.hubspot.com/marketing/creative-brief" className="link-table">How to Write a Creative Brief, With Helpful Templates and Examples</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.toptal.com/product-managers/product-development/write-killer-product-requirement-document-prd" className="link-table">How to Write a Killer Product Requirements Document</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-project-brief" 
            id="tab-project-brief-template"
          />
          <label htmlFor="tab-project-brief-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_knh7e64=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Project Brief Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Deliverables Section */}
      <main className="case-study">
        <span className="case-tool-title" id="deliverables">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="deliverables">
            <span className="tool-title-number">
              <Link to="#deliverables">Deliverables</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/project-scoping">Project Scoping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#deliverables"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-deliverables" 
            id="tab-deliverables-overview" 
            defaultChecked
          />
          <label htmlFor="tab-deliverables-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Project Scoping", "20/08/20", deliverablesTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                In the heat of the project, it's easy to lose site of the important things. Having clearly defined deliverables helps to keep focus throughout the project, ensuring those important things are checked off as we go. 
    
                </p>
                <p>
                It also helps with transparency amongst the team and stakeholders, with regularly delivered or iterated stages we can ensure people are still focused and onboard with the project, as well as being able to continue to be involved. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.youtube.com/watch?v=J2kx9-QXnSo" className="link-table">Creating Project Deliverables - Project Management Training</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://github.com/topics/product-management" className="link-table">GitHub Topics: Product Management</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-deliverables" 
            id="tab-deliverables-template"
          />
          <label htmlFor="tab-deliverables-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_knu7jLs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Deliverables Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProjectScopingPage; 